import { useState } from "react";

export type TFilteringRule = {
    filteringColumnHeader: string;
    filteringValue: string[];
    includeFilteredValues: boolean;
};

type TProps = {
    fileType: "tb" | "gl";
    availableColumns: string[];
    rules: TFilteringRule[];
    onChange: (rules: TFilteringRule[]) => void;
    readOnly?: boolean;
};

type TTagInputProps = {
    values: string[];
    onChange: (values: string[]) => void;
    disabled?: boolean;
};

function TagInput({ values, onChange, disabled }: TTagInputProps) {
    const [inputValue, setInputValue] = useState("");

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (disabled) return;
        if ((e.key === "Enter" || e.key === ",") && inputValue.trim()) {
            e.preventDefault();
            const newVal = inputValue.trim();
            if (!values.includes(newVal)) {
                onChange([...values, newVal]);
            }
            setInputValue("");
        } else if (e.key === "Backspace" && !inputValue && values.length > 0) {
            onChange(values.slice(0, -1));
        }
    };

    const removeValue = (idx: number) => {
        onChange(values.filter((_, i) => i !== idx));
    };

    return (
        <div
            className="flex flex-wrap gap-1 min-h-[36px] w-full rounded border border-gray-300 bg-white px-2 py-1 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500"
            onClick={() => {
                const el = document.activeElement as HTMLInputElement;
                el?.focus();
            }}
        >
            {values.map((v, i) => (
                <span
                    key={i}
                    className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded"
                >
                    {v}
                    {!disabled && (
                        <button
                            type="button"
                            onClick={() => removeValue(i)}
                            className="hover:text-blue-600 leading-none"
                        >
                            ×
                        </button>
                    )}
                </span>
            ))}
            {!disabled && (
                <input
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={() => {
                        if (inputValue.trim()) {
                            const newVal = inputValue.trim();
                            if (!values.includes(newVal)) {
                                onChange([...values, newVal]);
                            }
                            setInputValue("");
                        }
                    }}
                    className="flex-1 min-w-[80px] outline-none text-sm bg-transparent"
                    placeholder={values.length === 0 ? "Type a value and press Enter" : ""}
                />
            )}
        </div>
    );
}

export function OrganismFilteringRules({
    fileType,
    availableColumns,
    rules,
    onChange,
    readOnly = false,
}: TProps) {
    // A rule whose column is not in availableColumns is flagged, never dropped:
    // these rules are persisted configuration, and silently deleting them here
    // would discard them on the next save. The sample file's headers can also
    // legitimately lag the real files, so this is a warning, not an error.
    const columnSet = new Set(availableColumns);
    const isUnknownColumn = (header: string) =>
        availableColumns.length > 0 && !columnSet.has(header);

    const usedHeaders = new Set(rules.map((r) => r.filteringColumnHeader));

    const addRule = () => {
        const firstAvailable = availableColumns.find((c) => !usedHeaders.has(c));
        if (!firstAvailable) return;
        onChange([
            ...rules,
            {
                filteringColumnHeader: firstAvailable,
                filteringValue: [],
                includeFilteredValues: true,
            },
        ]);
    };

    const updateRule = (idx: number, patch: Partial<TFilteringRule>) => {
        onChange(rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    };

    const removeRule = (idx: number) => {
        onChange(rules.filter((_, i) => i !== idx));
    };

    const label = fileType === "tb" ? "TB" : "GL";

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-700">{label} Row Filtering Rules</h4>
                {!readOnly && availableColumns.length > 0 && (
                    <button
                        type="button"
                        onClick={addRule}
                        disabled={usedHeaders.size >= availableColumns.length}
                        className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                        + Add rule
                    </button>
                )}
            </div>

            {rules.length === 0 && (
                <p className="text-xs text-gray-400 italic">No filtering rules configured.</p>
            )}

            {rules.map((rule, idx) => {
                const unknownColumn = isUnknownColumn(rule.filteringColumnHeader);
                // Keep the rule's own header selectable even when the current file no
                // longer has it, so the value survives until the user re-points it.
                const availableForThisRow = [
                    ...(unknownColumn ? [rule.filteringColumnHeader] : []),
                    ...availableColumns.filter(
                        (c) => c === rule.filteringColumnHeader || !usedHeaders.has(c),
                    ),
                ];
                return (
                    <div key={idx} className="space-y-1">
                        <div className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-start">
                            {/* Column selector */}
                            <select
                                value={rule.filteringColumnHeader}
                                onChange={(e) =>
                                    updateRule(idx, { filteringColumnHeader: e.target.value })
                                }
                                disabled={readOnly}
                                className={`h-9 rounded border bg-white px-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 ${
                                    unknownColumn ? "border-amber-500" : "border-gray-300"
                                }`}
                            >
                                {availableForThisRow.map((col) => (
                                    <option key={col} value={col}>
                                        {col}
                                    </option>
                                ))}
                            </select>

                            {/* Value tag input */}
                            <TagInput
                                values={rule.filteringValue}
                                onChange={(v) => updateRule(idx, { filteringValue: v })}
                                disabled={readOnly}
                            />

                            {/* Include / exclude */}
                            <select
                                value={rule.includeFilteredValues ? "include" : "exclude"}
                                onChange={(e) =>
                                    updateRule(idx, {
                                        includeFilteredValues: e.target.value === "include",
                                    })
                                }
                                disabled={readOnly}
                                className="h-9 rounded border border-gray-300 bg-white px-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                            >
                                <option value="include">Include</option>
                                <option value="exclude">Exclude</option>
                            </select>

                            {/* Delete */}
                            {!readOnly && (
                                <button
                                    type="button"
                                    onClick={() => removeRule(idx)}
                                    className="h-9 px-2 text-gray-400 hover:text-red-500"
                                    title="Remove rule"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                        {unknownColumn && (
                            <p className="text-xs text-amber-600">
                                Column “{rule.filteringColumnHeader}” is not in the current file’s
                                headers. An Include rule on a missing column removes every row —
                                re-point or remove this rule.
                            </p>
                        )}
                        {rule.filteringValue.length === 0 && (
                            <p className="text-xs text-amber-600">
                                No values set — add at least one value or remove this rule.
                            </p>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
